import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { Baseurl } from '../../../Api/Baseurl';
import { toast } from 'react-toastify';
import { ToastOptions } from '../../../helpers/ToastOptions';
import Loading from '../../../components/Loading/Loading';
import { motion } from 'framer-motion';
import { MdEdit, MdDelete, MdVisibility } from 'react-icons/md';
import Swal from 'sweetalert2';
import { gradesList } from '../../../helpers/gradesList';
import { useRoleBasedRoute } from '../../../utils/routeUtils';
import { UserContext } from '../../../Context/Usercontext';
import './Mymodules.scss';

const Mymodules = () => {
    const token = localStorage.getItem('medtoken');
    const { userId } = useContext(UserContext);
    const [selectedGrade, setSelectedGrade] = useState('');
    const [showGroupedView, setShowGroupedView] = useState(false);
    const [allModulesData, setAllModulesData] = useState({});
    const { getRoute } = useRoleBasedRoute();

    // Fetch modules
    const fetchModules = async () => {
        const params = new URLSearchParams();
        if (selectedGrade) {
            params.append('grade', selectedGrade);
        }

        // Get moderator ID from user context
        const moderatorId = userId;
        
        if (!moderatorId) {
            throw new Error('User ID not found');
        }
        
        const response = await axios.get(`${Baseurl}/module-moderator-analysis/moderators/${moderatorId}/modules?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        
        const { modulesByGrade } = response.data.payload;
        
      
        setAllModulesData(modulesByGrade);
        
    
        if (selectedGrade) {
            return modulesByGrade[selectedGrade] || [];
        }
        
        // If no grade filter, return all modules flattened
        return Object.values(modulesByGrade).flat();
    };

    const { data: modules = [], isLoading, refetch } = useQuery(
        ['modules', selectedGrade],
        fetchModules,
        {
            refetchOnWindowFocus: false,
            onError: (error) => {
                toast.error('Failed to fetch modules', ToastOptions('error'));
            }
        }
    );

    // Delete module handler
    const handleDeleteModule = async (moduleId, moduleTitle) => {
        Swal.fire({
            title: "Are you sure?",
            text: `This will permanently delete "${moduleTitle}" and all its content`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#01677e",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axios.delete(`${Baseurl}/modules/${moduleId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Module deleted successfully', ToastOptions('success'));
                    refetch();
                } catch (error) {
                    toast.error(error.response?.data?.msg || 'Failed to delete module', ToastOptions('error'));
                }
            }
        });
    };

    const handleGradeChange = (e) => {
        setSelectedGrade(e.target.value);
    };

    const clearFilters = () => {
        setSelectedGrade('');
    };

    // Render module card component
    const renderModuleCard = (module) => (
        <>
            <div className="module-header">
                <h3 className="module-title">{module.title}</h3>
                <div className="module-badges">
                    <span className="grade-badge">Grade {module.grade}</span>
                    {module.isFree ? (
                        <span className="free-badge">Free</span>
                    ) : (
                        <span className="paid-badge">Paid</span>
                    )}
                </div>
            </div>

            {(module.Coverimage || module.image) && (
                <div className="module-image">
                    <img 
                        src={`${Baseurl}/${(module.image || module.Coverimage).replace(/\\/g, '/')}`} 
                        alt={module.title} 
                    />
                </div>
            )}

            <div className="module-content">
                <div className="module-updated">
                    <span className="module-updated-label">Description:</span>
                    <span className="module-updated-date">{module.description || 'N/A'}</span>
                </div>
                <div className="module-updated">
                    <span className="module-updated-label">Created at:</span>
                    <span className="module-updated-date">{module.createdAt ? new Date(module.createdAt).toLocaleString() : 'N/A'}</span>
                </div>
                <div className="module-stats">
                    <div className="stat-item">
                        <span className="stat-label">Chapters:</span>
                        <span className="stat-value">{module.contentCount?.chapters || 0}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">QBanks:</span>
                        <span className="stat-value">{module.contentCount?.qBanks || 0}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Flashcards:</span>
                        <span className="stat-value">{module.contentCount?.flashcardSets || 0}</span>
                    </div>
                </div>
            </div>

            <div className="module-actions">
                <Link
                    to={getRoute(`modules/${module.id}/content`)}
                    className="view-content-btn"
                    title="View Content"
                >
                    <MdVisibility />
                    <span>View Content</span>
                </Link>
                <Link
                    to={getRoute(`modules/${module.id}/edit`)}
                    className="edit-btn"
                    title="Edit Module"
                >
                    <MdEdit />
                    <span>Edit</span>
                </Link>
                <button
                    onClick={() => handleDeleteModule(module.id, module.title)}
                    className="delete-btn"
                    title="Delete Module"
                >
                    <MdDelete />
                    <span>Delete</span>
                </button>
            </div>
        </>
    );

    if (isLoading) return <Loading />;

    return (
        <div className="modules-container">
            <div className="modules-header">
                <div className="header-content">
                    <h1>Modules</h1>
                    <p>Manage your educational modules and their content</p>
                </div>
                <div className="header-actions">
                    {!selectedGrade && Object.keys(allModulesData).length > 0 && (
                        <button
                            onClick={() => setShowGroupedView(!showGroupedView)}
                            className="toggle-grouped-btn"
                        >
                            {showGroupedView ? 'Show All' : 'Group by Grade'}
                        </button>
                    )}
                </div>
            </div>

            <div className="filters-section">
                <div className="grade-filter">
                    <label htmlFor="grade-select" className="block mb-2 text-sm font-medium text-gray-700">
                        Filter by Grade
                    </label>
                    <select
                        id="grade-select"
                        value={selectedGrade}
                        onChange={handleGradeChange}
                        className="grade-select"
                    >
                        <option value="">All Grades</option>
                        {gradesList.map((group, groupIndex) => (
                            <optgroup key={groupIndex} label={group.group}>
                                {group.options.map((option, optionIndex) => (
                                    <option key={optionIndex} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>

                {(selectedGrade) && (
                    <div className="filter-actions">
                        <button
                            onClick={clearFilters}
                            className="clear-filters-btn"
                        >
                            Clear Filters
                        </button>
                    </div>
                )}
            </div>

            <div className="modules-grid">
                {modules.length === 0 ? (
                    <div className="no-data-message">
                        <p>No data</p>
                    </div>
                ) : showGroupedView && !selectedGrade ? (
                    // Grouped view by grade
                    Object.entries(allModulesData).map(([grade, gradeModules]) => (
                        <div key={grade} className="grade-group">
                            <h2 className="grade-group-title">Grade {grade}</h2>
                            <div className="grade-modules-grid">
                                {gradeModules.map((module, index) => (
                                    <motion.div
                                        key={module.id}
                                        className="module-card"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.1 }}
                                    >
                                        {renderModuleCard(module)}
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    // Regular view
                    modules.map((module, index) => (
                        <motion.div
                            key={module.id}
                            className="module-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                            {renderModuleCard(module)}
                        </motion.div>
                    ))
                )}
            </div>
        </div>
    );
};

export default Mymodules; 